
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, ShoppingCart, FileText, Eye, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";

interface OrderItem {
  id: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  packSize: string;
}

interface Order {
  id: string;
  date: string;
  items: OrderItem[];
  total: number;
  status: "Delivered" | "Cancelled" | "Refunded";
  paymentMethod: string;
  sellerName: string;
}

const PastOrders: React.FC = () => {
  const { toast } = useToast();
  const pastOrders: Order[] = [
    {
      id: "ORD-123456",
      date: "2025-05-10",
      items: [
        { id: "1", productName: "Fresh Milk", quantity: 10, unitPrice: 6.90, packSize: "1L" },
        { id: "2", productName: "White Bread", quantity: 15, unitPrice: 3.50, packSize: "400g" },
      ],
      total: 121.50,
      status: "Delivered",
      paymentMethod: "Credit",
      sellerName: "Kedai Jimat Sdn Bhd"
    },
    {
      id: "ORD-123455",
      date: "2025-05-08",
      items: [
        { id: "3", productName: "Eggs", quantity: 8, unitPrice: 5.80, packSize: "10pcs" },
        { id: "4", productName: "Milo", quantity: 5, unitPrice: 23.90, packSize: "1kg" },
        { id: "5", productName: "Cooking Oil", quantity: 6, unitPrice: 28.90, packSize: "5kg" },
      ],
      total: 293.90,
      status: "Delivered",
      paymentMethod: "Cash on Delivery",
      sellerName: "The Grocery Supplier"
    },
    {
      id: "ORD-123450",
      date: "2025-05-05",
      items: [
        { id: "6", productName: "Rice", quantity: 3, unitPrice: 45.90, packSize: "10kg" },
        { id: "7", productName: "Canned Tuna", quantity: 12, unitPrice: 9.90, packSize: "185g" },
      ],
      total: 256.50,
      status: "Delivered",
      paymentMethod: "Credit",
      sellerName: "Kedai Jimat Sdn Bhd"
    },
    {
      id: "ORD-123445",
      date: "2025-05-01",
      items: [
        { id: "3", productName: "Eggs", quantity: 5, unitPrice: 5.80, packSize: "10pcs" },
        { id: "8", productName: "Sugar", quantity: 4, unitPrice: 4.90, packSize: "1kg" },
      ],
      total: 48.60,
      status: "Cancelled",
      paymentMethod: "Credit",
      sellerName: "The Grocery Supplier"
    }
  ];

  const [searchTerm, setSearchTerm] = useState("");
  const [viewOrderId, setViewOrderId] = useState<string | null>(null);
  const [viewInvoiceId, setViewInvoiceId] = useState<string | null>(null);
  const [reorderItems, setReorderItems] = useState<OrderItem[]>([]);
  const [selectedReorderItems, setSelectedReorderItems] = useState<Record<string, boolean>>({});
  const [isReorderDialogOpen, setIsReorderDialogOpen] = useState(false);

  const filteredOrders = pastOrders.filter(order => 
    order.id.toLowerCase().includes(searchTerm.toLowerCase()) || 
    order.date.includes(searchTerm) ||
    order.sellerName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewOrder = (orderId: string) => {
    setViewOrderId(orderId);
  };

  const handleViewInvoice = (orderId: string) => {
    setViewInvoiceId(orderId);
  };

  const handleReorder = (orderId: string) => {
    const order = pastOrders.find(o => o.id === orderId);
    if (order) {
      const initialSelectedItems: Record<string, boolean> = {};
      order.items.forEach(item => {
        initialSelectedItems[item.id] = false;
      });
      setReorderItems(order.items);
      setSelectedReorderItems(initialSelectedItems);
      setIsReorderDialogOpen(true);
    }
  };

  const toggleSelectAllReorderItems = () => {
    const allSelected = reorderItems.every(item => selectedReorderItems[item.id]);
    const updatedSelection: Record<string, boolean> = {};
    
    reorderItems.forEach(item => {
      updatedSelection[item.id] = !allSelected;
    });
    
    setSelectedReorderItems(updatedSelection);
  };

  const toggleSelectReorderItem = (itemId: string) => {
    setSelectedReorderItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleAddToCart = () => {
    const selectedItems = reorderItems.filter(item => selectedReorderItems[item.id]);
    if (selectedItems.length > 0) {
      toast({
        description: `${selectedItems.length} items added to cart.`,
      });
      setIsReorderDialogOpen(false);
    } else {
      toast({
        description: "Please select at least one item to add to cart.",
        variant: "destructive"
      });
    }
  };

  const getOrderById = (orderId: string) => {
    return pastOrders.find(order => order.id === orderId);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Past Orders</h3>
      
      <div className="flex w-full max-w-sm items-center space-x-2 mb-4">
        <Input
          type="text"
          placeholder="Search orders by ID, date, or seller..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Button type="submit" className="bg-green-600 hover:bg-green-700">
          <Search className="h-4 w-4" />
        </Button>
      </div>
      
      <Card className="border-green-100">
        <CardContent className="p-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-green-50">
                  <TableHead>Order ID</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total (RM)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment Method</TableHead>
                  <TableHead>Seller's Name</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="font-medium">{order.id}</TableCell>
                    <TableCell>{order.date}</TableCell>
                    <TableCell>{order.items.length} items</TableCell>
                    <TableCell>{order.total.toFixed(2)}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        order.status === 'Delivered' ? 'bg-green-100 text-green-800' : 
                        order.status === 'Cancelled' ? 'bg-red-100 text-red-800' : 
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {order.status}
                      </span>
                    </TableCell>
                    <TableCell>{order.paymentMethod}</TableCell>
                    <TableCell>{order.sellerName}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" onClick={() => handleViewOrder(order.id)}>
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleViewInvoice(order.id)}>
                          <FileText className="h-4 w-4 mr-1" />
                          Invoice
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleReorder(order.id)}
                        >
                          <ShoppingCart className="h-4 w-4 mr-1" />
                          Reorder
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Purchase Order Dialog */}
      <Dialog open={viewOrderId !== null} onOpenChange={() => setViewOrderId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Purchase Order - {viewOrderId}</DialogTitle>
          </DialogHeader>
          {viewOrderId && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Order ID:</p>
                  <p className="font-medium">{viewOrderId}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date:</p>
                  <p className="font-medium">{getOrderById(viewOrderId)?.date}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Seller:</p>
                  <p className="font-medium">{getOrderById(viewOrderId)?.sellerName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Payment Method:</p>
                  <p className="font-medium">{getOrderById(viewOrderId)?.paymentMethod}</p>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="bg-green-50">
                    <TableHead>Product</TableHead>
                    <TableHead>Pack Size</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Unit Price (RM)</TableHead>
                    <TableHead>Subtotal (RM)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getOrderById(viewOrderId)?.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.productName}</TableCell>
                      <TableCell>{item.packSize}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.unitPrice.toFixed(2)}</TableCell>
                      <TableCell>{(item.quantity * item.unitPrice).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4} className="text-right font-medium">
                      Total:
                    </TableCell>
                    <TableCell className="font-bold">
                      {getOrderById(viewOrderId)?.total.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={viewInvoiceId !== null} onOpenChange={() => setViewInvoiceId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Invoice - {viewInvoiceId}</DialogTitle>
          </DialogHeader>
          {viewInvoiceId && (
            <div className="space-y-4">
              <div className="flex justify-between pb-4 border-b">
                <div>
                  <h3 className="font-bold text-lg">INVOICE</h3>
                  <p className="text-muted-foreground">JiMATBiz Wholesale</p>
                  <p className="text-muted-foreground">123 Business Park</p>
                  <p className="text-muted-foreground">Kuala Lumpur, Malaysia</p>
                </div>
                <div className="text-right">
                  <p><span className="text-muted-foreground">Invoice #:</span> INV-{viewInvoiceId.split('-')[1]}</p>
                  <p><span className="text-muted-foreground">Date:</span> {getOrderById(viewInvoiceId)?.date}</p>
                  <p><span className="text-muted-foreground">Order #:</span> {viewInvoiceId}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                <div>
                  <p className="font-semibold">Seller:</p>
                  <p>{getOrderById(viewInvoiceId)?.sellerName}</p>
                  <p className="text-muted-foreground">Seller Address Line 1</p>
                  <p className="text-muted-foreground">Seller Address Line 2</p>
                </div>
                <div>
                  <p className="font-semibold">Buyer:</p>
                  <p>Your Retail Store</p>
                  <p className="text-muted-foreground">Buyer Address Line 1</p>
                  <p className="text-muted-foreground">Buyer Address Line 2</p>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="bg-green-50">
                    <TableHead>Product</TableHead>
                    <TableHead>Pack Size</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Unit Price (RM)</TableHead>
                    <TableHead>Subtotal (RM)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getOrderById(viewInvoiceId)?.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.productName}</TableCell>
                      <TableCell>{item.packSize}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.unitPrice.toFixed(2)}</TableCell>
                      <TableCell>{(item.quantity * item.unitPrice).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4} className="text-right font-medium">
                      Subtotal:
                    </TableCell>
                    <TableCell>
                      {getOrderById(viewInvoiceId)?.total.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} className="text-right font-medium">
                      Tax (0%):
                    </TableCell>
                    <TableCell>
                      0.00
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} className="text-right font-medium">
                      Total:
                    </TableCell>
                    <TableCell className="font-bold">
                      {getOrderById(viewInvoiceId)?.total.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <div className="text-center mt-8">
                <p className="text-muted-foreground">Thank you for your business!</p>
                <p className="text-xs text-muted-foreground">This is a computer-generated invoice, no signature required.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reorder Dialog */}
      <Dialog open={isReorderDialogOpen} onOpenChange={setIsReorderDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Reorder Items</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="select-all" 
                checked={reorderItems.length > 0 && reorderItems.every(item => selectedReorderItems[item.id])}
                onCheckedChange={toggleSelectAllReorderItems}
              />
              <label htmlFor="select-all" className="text-sm font-medium">Select All</label>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="bg-green-50">
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Pack Size</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Unit Price (RM)</TableHead>
                  <TableHead>Subtotal (RM)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reorderItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Checkbox 
                        checked={selectedReorderItems[item.id] || false}
                        onCheckedChange={() => toggleSelectReorderItem(item.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{item.productName}</TableCell>
                    <TableCell>{item.packSize}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.unitPrice.toFixed(2)}</TableCell>
                    <TableCell>{(item.quantity * item.unitPrice).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsReorderDialogOpen(false)}>
                Cancel
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handleAddToCart}>
                <ShoppingCart className="h-4 w-4 mr-1" />
                Add to Cart
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PastOrders;
